import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as fromAnnualReportActions from '../../../../aumico-lib-annualreport-fe/src/lib/actions/annualReport.actions';
import * as fromLicenceActions from '../actions/licence.actions';
import * as fromActions from '../actions/organization.actions';
import { GetOrganizationDto } from '../dtos/getOrganizationDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { InvitationsDto } from '../dtos/invitationsDto.interface';
import { OrganizationSearchResultDto } from '../dtos/organizationSearchDto.interface';
import { referralDto } from '../dtos/referralDto.interface';
import { CPMMState } from '../enums/cpmm-state.enum';
import { FCPState } from '../enums/subscription-state.enum';
import { OrganizationSettings } from '../interfaces/organization-settings.interface';
import { UpdateOrganizationLocaleDto } from '../interfaces/update-organization-locale-request.interface';
import { IUpdateOrganizationState } from '../interfaces/update-organization-state-request.interface';
import { UpdateOrganizationTierDto } from '../interfaces/update-organization-tier-request.interface';
import { IUploadOrganizationReportingTemplateDto } from '../interfaces/upload-organization-reporting-template-dto.interface';
import { OrganizationService } from '../services/organization.service';

const debug = Debug('modeso:aumico-lib-users-fe:OrganizationEffects');

@Injectable()
export class OrganizationEffects {
    constructor(private readonly actions$: Actions, private readonly service: OrganizationService) { }

    updateFcpState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCurrentOrganization.success),
            map((action) => {
                // Extract FCP Activation Details from the current organization
                const fcpActivationDetails = action.payload?.fcpActivationDetails;

                let active = false;
                try {
                    if (
                        fcpActivationDetails?.subscriptionState === FCPState.PAID ||
                        fcpActivationDetails?.subscriptionState === FCPState.TRIAL
                    ) {
                        active = true;
                    }
                } catch (error) {
                    console.error('Error in updateFcpState$', error);
                }

                // Dispatch an action to update the annual report state
                return fromAnnualReportActions.updateFcpState({ payload: active });
            }),
        ),
    );

    getCurrentOrganization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCurrentOrganization.do.type),
            mergeMap(() => {
                return this.service.getCurrentOrganization$().pipe(
                    map((response: GetOrganizationDto) => {
                        return fromActions.getCurrentOrganization.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getCurrentOrganization.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    // NEW: whenever the org loads or is set, re-dispatch getLicenceStatus
    autoLoadOnOrgChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCurrentOrganization.success),
            // you could also filter out undefined payloads here,
            // but both of these always carry a valid org on success/set
            map(() => fromLicenceActions.licenceActions.getLicenceStatus()),
        ),
    );

    getUserById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getUserById.do.type),
            mergeMap(({ payload }) => {
                return this.service.getUserById$(payload).pipe(
                    map((response: GetUserDto) => fromActions.getUserById.success({ payload: response })),
                    catchError((error: HttpErrorResponse) => of(fromActions.getUserById.failure({ payload: error }))),
                );
            }),
        ),
    );

    updateCurrentOrganization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateCurrentOrganization.do.type),
            mergeMap((payload) => {
                return this.service.updateCurrentOrganization$(payload).pipe(
                    map(() => {
                        return fromActions.updateCurrentOrganization.success(payload);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateCurrentOrganization.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAllOrganization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAllOrganizations.do.type),
            mergeMap(() => {
                return this.service.getAllOrganizations$().pipe(
                    map((response: GetOrganizationDto[]) => {
                        return fromActions.getAllOrganizations.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getAllOrganizations.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    createSearchIndex$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createIndexAction.do.type),
            mergeMap(() => {
                return this.service.createSearchIndex$().pipe(
                    map((response: void) => {
                        return fromActions.createIndexAction.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.createIndexAction.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getSearchOrganization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.searchOrganizations.search.type),
            mergeMap(({ payload }) => {
                return this.service.searchOrganizations$(payload).pipe(
                    map((response: OrganizationSearchResultDto[]) => {
                        return fromActions.searchOrganizations.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.searchOrganizations.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateOrganizationState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateOrganizationState.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IUpdateOrganizationState>) => {
                return this.service.updateOrganizationState$(payload).pipe(
                    map(() => {
                        return fromActions.updateOrganizationState.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateOrganizationState.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateOrganizationTier$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateOrganizationTier.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<UpdateOrganizationTierDto>) => {
                return this.service.updateOrganizationTier$(payload).pipe(
                    map(() => {
                        return fromActions.updateOrganizationTier.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateOrganizationTier.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateOrganizationTierForOwnOrg$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateOrganizationTier.forownorg.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<UpdateOrganizationTierDto>) => {
                return this.service.updateOrganizationSubscriptionTier$(payload).pipe(
                    map(() => {
                        return fromActions.updateOrganizationTier.successforownorg({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateOrganizationTier.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );


    updateOrganizationLocale$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateOrganizationLocale.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<UpdateOrganizationLocaleDto>) => {
                return this.service.updateOrganizationLocale$(payload).pipe(
                    map(() => {
                        return fromActions.updateOrganizationLocale.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateOrganizationLocale.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateOrganizationAbcp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateOrganizationAbcp.do),
            mergeMap(({ payload }) =>
                this.service.updateOrganizationAbcp$(payload).pipe(
                    map(() => fromActions.updateOrganizationAbcp.success({ payload })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.updateOrganizationAbcp.failure({ payload, error })),
                    ),
                ),
            ),
        ),
    );

    updateOrganizationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateOrganizationSettings.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<OrganizationSettings>) => {
                return this.service.updateOrganizationSettings$(payload).pipe(
                    map(() => {
                        return fromActions.updateOrganizationSettings.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateOrganizationSettings.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getOrganizationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getOrganizationSettings.do.type),
            mergeMap(() => {
                return this.service.getOrganizationSettings$().pipe(
                    map((response: OrganizationSettings) => {
                        return fromActions.getOrganizationSettings.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getOrganizationSettings.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getUsers.do),
            mergeMap((action) => {
                const includeDeleted = action.includeDeleted || false;
                return this.service.getUsers$(includeDeleted).pipe(
                    map((response: GetUserDto[]) => {
                        return fromActions.getUsers.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getUsers.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    inviteUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.inviteUsers.do.type),
            mergeMap((payload) => {
                return this.service.inviteUsers$(payload).pipe(
                    map((response: InvitationsDto) => {
                        return fromActions.inviteUsers.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.inviteUsers.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    withdrawInvite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.inviteUsers.withdraw.type),
            mergeMap(({ payload }) => {
                return this.service.withdrawInvitation$(payload).pipe(
                    map(() => {
                        return fromActions.inviteUsers.withdrawsuccess({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.inviteUsers.withdrawfailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    resendInvite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.inviteUsers.resendinvitation.type),
            mergeMap(({ payload }) => {
                return this.service.resendInvitation$(payload).pipe(
                    map((response) => {
                        return fromActions.inviteUsers.resendinvitationsuccess({
                            newInvitation: response,
                            oldUuid: payload,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.inviteUsers.resendinvitationfailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    inviteReferrals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.inviteReferrals.do.type),
            mergeMap((payload) => {
                return this.service.inviteReferrals$(payload).pipe(
                    map((response: referralDto) => {
                        return fromActions.inviteReferrals.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.inviteReferrals.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    acceptReferrals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.inviteReferrals.accept.type),
            mergeMap((payload) => {
                return this.service.acceptReferrals$(payload).pipe(
                    map((response: string) => {
                        return fromActions.inviteReferrals.acceptReferralInvitationSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.inviteReferrals.acceptReferralInvitationFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    removeUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.removeUsers.do.type),
            mergeMap((payload) => {
                return this.service.removeUsers$(payload).pipe(
                    map((response) => {
                        return fromActions.removeUsers.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.removeUsers.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateUserRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateUserRole.do.type),
            mergeMap(({ payload }: { payload: { userId: string; role: any } }) => {
                return this.service.updateUserRole$(payload.userId, payload.role).pipe(
                    map((response) =>
                        fromActions.updateUserRole.success({ payload: response }),
                    ),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.updateUserRole.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    uploadOrganizationOrReportingTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.uploadOrganizationOrReportingTemplate.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IUploadOrganizationReportingTemplateDto>) => {
                return this.service.uploadOrganizationOrReportingTemplate$(payload).pipe(
                    map(() => {
                        return fromActions.uploadOrganizationOrReportingTemplate.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.uploadOrganizationOrReportingTemplate.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    uploadOrganizationHgbReportingTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.uploadOrganizationHgbReportingTemplate.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IUploadOrganizationReportingTemplateDto>) => {
                return this.service.uploadOrganizationHgbReportingTemplate$(payload).pipe(
                    map(() => {
                        return fromActions.uploadOrganizationHgbReportingTemplate.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.uploadOrganizationHgbReportingTemplate.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    setOrganizationReportingTemplateToDefault$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setOrganizationReportingTemplateToDefault.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<string>) => {
                return this.service.setOrganizationReportingTemplateToDefault$(payload).pipe(
                    map(() => {
                        return fromActions.setOrganizationReportingTemplateToDefault.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.setOrganizationReportingTemplateToDefault.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    setOrganizationHgbReportingTemplateToDefault$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setOrganizationHgbReportingTemplateToDefault.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<string>) => {
                return this.service.setOrganizationHgbReportingTemplateToDefault$(payload).pipe(
                    map(() => {
                        return fromActions.setOrganizationHgbReportingTemplateToDefault.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.setOrganizationHgbReportingTemplateToDefault.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    uploadOrganizationOrExcelTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.uploadOrganizationOrExcelTemplate.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IUploadOrganizationReportingTemplateDto>) => {
                return this.service.uploadOrganizationOrExcelTemplate$(payload).pipe(
                    map(() => {
                        return fromActions.uploadOrganizationOrExcelTemplate.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.uploadOrganizationOrExcelTemplate.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    uploadOrganizationHgbExcelTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.uploadOrganizationHgbExcelTemplate.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<IUploadOrganizationReportingTemplateDto>) => {
                return this.service.uploadOrganizationHgbExcelTemplate$(payload).pipe(
                    map(() => {
                        return fromActions.uploadOrganizationHgbExcelTemplate.success({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.uploadOrganizationHgbExcelTemplate.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    setOrganizationExcelTemplateToDefault$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setOrganizationExcelTemplateToDefault.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<string>) => {
                return this.service.setOrganizationExcelTemplateToDefault$(payload).pipe(
                    map(() => {
                        return fromActions.setOrganizationExcelTemplateToDefault.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.setOrganizationExcelTemplateToDefault.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    setOrganizationHgbExcelTemplateToDefault$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setOrganizationHgbExcelTemplateToDefault.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<string>) => {
                return this.service.setOrganizationHgbExcelTemplateToDefault$(payload).pipe(
                    map(() => {
                        return fromActions.setOrganizationHgbExcelTemplateToDefault.success();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.setOrganizationHgbExcelTemplateToDefault.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deactivateFCP$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.fcpManagement.deactivateFcp.type),
            mergeMap(({ surveyData }) =>
                this.service.deactivateFCP$(surveyData).pipe(
                    mergeMap((response) => [
                        // Dispatch success action
                        fromActions.fcpManagement.deactivateFcpSuccess({
                            fcpActivation: response,
                        }),
                        // Dispatch action to update FCP state
                        fromAnnualReportActions.updateFcpState({ payload: false }),
                    ]),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.fcpManagement.deactivateFcpFailure({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    activateFCPTrial$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.fcpManagement.askForFcpApproval.type),
            mergeMap(() => {
                return this.service.activateFCPTrial$().pipe(
                    map((response) => {
                        return fromActions.fcpManagement.askForFcpApprovalSuccess({
                            fcpActivation: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.fcpManagement.askForFcpApprovalFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    changeFCPState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.fcpManagement.changeStateFcp.type),
            mergeMap((payload: { organizationId: string; fcpState: FCPState }) => {
                return this.service.changeFCPState$(payload.organizationId, payload.fcpState).pipe(
                    map((response) => {
                        return fromActions.fcpManagement.updateFcpSuccess({
                            organizationId: payload.organizationId,
                            fcpActivation: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.fcpManagement.updateFcpFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    changeCPMMState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.cpmmActivation.changeCpmmState.type),
            mergeMap((payload: { organizationId: string; cpmmState: CPMMState }) =>
                this.service.changeCPMMState$(payload.organizationId, payload.cpmmState).pipe(
                    mergeMap((response) => [
                        // Dispatch success action
                        fromActions.cpmmActivation.changeCpmmStateSuccess({
                            organizationId: payload.organizationId,
                            cpmmActivation: response,
                        }),
                    ]),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.cpmmActivation.changeCpmmStateFailure({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    bulkUpdateUserRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.bulkUpdateUserRole.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<{ userIds: string[]; role: string }>) => {
                return this.service.bulkUpdateUserRole$(payload).pipe(
                    map((response: GetUserDto[]) =>
                        fromActions.bulkUpdateUserRole.success({ payload: response }),
                    ),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.bulkUpdateUserRole.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    handleOnLoadAllErrors(error) {
        debug('handleOnLoadAllErrors');
        debug(error);
        return error;
    }
}
